import { createFeature } from './featureService';
import { logger } from '../utils/logger';

/**
 * Validation error structure
 */
export class ValidationError {
  constructor(externalId, name, message) {
    this.externalId = externalId;
    this.name = name;
    this.message = message;
  }
}

/**
 * Validate a single PortableFeature
 */
export const validatePortableFeature = (feature, index, allFeatures) => {
  const errors = [];
  
  if (!feature.externalId || typeof feature.externalId !== 'string') {
    errors.push(new ValidationError(
      feature.externalId || `[missing at index ${index}]`,
      feature.name || 'Unknown',
      'Missing or invalid externalId'
    ));
  }
  
  if (!feature.name || typeof feature.name !== 'string') {
    errors.push(new ValidationError(
      feature.externalId || `[missing at index ${index}]`,
      feature.name || 'Unknown',
      'Missing or invalid name'
    ));
  }
  
  if (feature.description !== undefined && typeof feature.description !== 'string') {
    errors.push(new ValidationError(
      feature.externalId,
      feature.name,
      'Description must be a string'
    ));
  }
  
  if (feature.position !== undefined && (typeof feature.position !== 'number' || feature.position < 0)) {
    errors.push(new ValidationError(
      feature.externalId,
      feature.name,
      'Position must be a non-negative number'
    ));
  }
  
  if (feature.type && !['feature', 'epic', 'group'].includes(feature.type)) {
    errors.push(new ValidationError(
      feature.externalId,
      feature.name,
      `Invalid type: ${feature.type}. Must be 'feature', 'epic', or 'group'`
    ));
  }
  
  if (feature.createdAt && typeof feature.createdAt !== 'string') {
    errors.push(new ValidationError(
      feature.externalId,
      feature.name,
      'createdAt must be a string'
    ));
  }
  
  return errors;
};

/**
 * Check for duplicate externalIds
 */
export const checkDuplicateExternalIds = (features) => {
  const seen = new Set();
  const duplicates = [];
  
  features.forEach((feature, index) => {
    if (feature.externalId) {
      if (seen.has(feature.externalId)) {
        duplicates.push(new ValidationError(
          feature.externalId,
          feature.name || 'Unknown',
          'Duplicate externalId found'
        ));
      } else {
        seen.add(feature.externalId);
      }
    }
  });
  
  return duplicates;
};

/**
 * Check for missing parents and circular references
 */
export const validateParentReferences = (features) => {
  const errors = [];
  const externalIdMap = new Map();
  
  // Build map of external IDs
  features.forEach(feature => {
    if (feature.externalId) {
      externalIdMap.set(feature.externalId, feature);
    }
  });
  
  // Check each feature's parent reference
  features.forEach(feature => {
    if (feature.parentExternalId) {
      // Check if parent exists
      if (!externalIdMap.has(feature.parentExternalId)) {
        errors.push(new ValidationError(
          feature.externalId,
          feature.name,
          `Parent with externalId '${feature.parentExternalId}' not found`
        ));
      } else {
        // Check for circular references
        const visited = new Set();
        let current = feature;
        
        while (current && current.parentExternalId) {
          if (visited.has(current.externalId)) {
            errors.push(new ValidationError(
              feature.externalId,
              feature.name,
              'Circular reference detected in parent chain'
            ));
            break;
          }
          visited.add(current.externalId);
          current = externalIdMap.get(current.parentExternalId);
        }
      }
    }
  });
  
  return errors;
};

/**
 * Validate all features and return errors
 */
export const validateFeatures = (features) => {
  const errors = [];
  
  if (!Array.isArray(features)) {
    return [new ValidationError('', 'File', 'Features must be an array')];
  }
  
  // Validate each feature
  features.forEach((feature, index) => {
    errors.push(...validatePortableFeature(feature, index, features));
  });
  
  // Check for duplicates
  errors.push(...checkDuplicateExternalIds(features));
  
  // Check parent references
  errors.push(...validateParentReferences(features));
  
  return errors;
};

/**
 * Fix validation issues (duplicates, missing parents, circular refs)
 */
export const fixValidationIssues = (features) => {
  const fixed = features.map(f => ({ ...f }));
  const externalIdMap = new Map();
  const seenExternalIds = new Set();
  
  // First pass: handle duplicates
  fixed.forEach(feature => {
    if (feature.externalId) {
      if (seenExternalIds.has(feature.externalId)) {
        // Generate new external ID
        const baseId = feature.externalId;
        let counter = 1;
        let newId = `${baseId}-${counter}`;
        while (seenExternalIds.has(newId)) {
          counter++;
          newId = `${baseId}-${counter}`;
        }
        feature.externalId = newId;
      }
      seenExternalIds.add(feature.externalId);
      externalIdMap.set(feature.externalId, feature);
    }
  });
  
  // Second pass: handle missing parents and circular references
  fixed.forEach(feature => {
    if (feature.parentExternalId) {
      // Check if parent exists
      if (!externalIdMap.has(feature.parentExternalId)) {
        // Remove parent reference
        feature.parentExternalId = undefined;
      } else {
        // Check for circular reference
        const visited = new Set();
        let current = feature;
        
        while (current && current.parentExternalId) {
          if (visited.has(current.externalId)) {
            // Circular reference detected, remove parent
            feature.parentExternalId = undefined;
            break;
          }
          visited.add(current.externalId);
          current = externalIdMap.get(current.parentExternalId);
        }
      }
    }
  });
  
  return fixed;
};

/**
 * Import features into the feature group
 */
export const importFeatures = async (features, featureGroupId, selectedExternalIds) => {
  const errors = [];
  const imported = [];
  
  // Filter to only selected features
  const featuresToImport = features.filter(f => 
    selectedExternalIds.has(f.externalId)
  );
  
  // Build external ID to internal ID mapping as we import
  const externalToInternalMap = new Map();
  
  // Sort features by position to maintain order
  const sortedFeatures = [...featuresToImport].sort((a, b) => 
    (a.position || 0) - (b.position || 0)
  );
  
  // Import root features first (no parent), then children
  const rootFeatures = sortedFeatures.filter(f => !f.parentExternalId);
  const childFeatures = sortedFeatures.filter(f => f.parentExternalId);
  
  // Import root features
  for (const feature of rootFeatures) {
    try {
      const featureData = {
        name: feature.name,
        description: feature.description || '',
        details: feature.metadata ? JSON.stringify({ metadata: feature.metadata }, null, 2) : null,
        feature_group_id: featureGroupId,
        parent_id: null,
        sort_order: feature.position || 0
      };
      
      const created = await createFeature(featureData);
      externalToInternalMap.set(feature.externalId, created.id);
      imported.push(created);
    } catch (err) {
      logger.error(`Error importing feature ${feature.externalId}:`, err);
      errors.push(new ValidationError(
        feature.externalId,
        feature.name,
        err.message || 'Failed to import feature'
      ));
    }
  }
  
  // Import child features (may need multiple passes for nested hierarchies)
  let remaining = [...childFeatures];
  let maxIterations = 100; // Prevent infinite loops
  let iteration = 0;
  
  while (remaining.length > 0 && iteration < maxIterations) {
    iteration++;
    const toImportThisPass = [];
    const stillRemaining = [];
    
    remaining.forEach(feature => {
      const parentInternalId = externalToInternalMap.get(feature.parentExternalId);
      if (parentInternalId) {
        toImportThisPass.push({ feature, parentInternalId });
      } else {
        stillRemaining.push(feature);
      }
    });
    
    // Import features whose parents are now available
    for (const { feature, parentInternalId } of toImportThisPass) {
      try {
        const featureData = {
          name: feature.name,
          description: feature.description || '',
          details: feature.metadata ? JSON.stringify({ metadata: feature.metadata }, null, 2) : null,
          feature_group_id: featureGroupId,
          parent_id: parentInternalId,
          sort_order: feature.position || 0
        };
        
        const created = await createFeature(featureData);
        externalToInternalMap.set(feature.externalId, created.id);
        imported.push(created);
      } catch (err) {
        logger.error(`Error importing feature ${feature.externalId}:`, err);
        errors.push(new ValidationError(
          feature.externalId,
          feature.name,
          err.message || 'Failed to import feature'
        ));
      }
    }
    
    remaining = stillRemaining;
  }
  
  // Report any features that couldn't be imported due to missing parents
  remaining.forEach(feature => {
    errors.push(new ValidationError(
      feature.externalId,
      feature.name,
      'Could not import: parent not found or circular reference'
    ));
  });
  
  return { imported, errors };
};

